import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import type { ReportList } from "@/types/report-list"

interface OpenReportListDialogProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly availableLists: ReportList[]
  readonly onConfirm: (selectedLists: ReportList[]) => void
}

export function OpenReportListDialog({
  open,
  onOpenChange,
  availableLists,
  onConfirm,
}: Readonly<OpenReportListDialogProps>) {
  const { t } = useTranslation()
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  // Sort lists alphabetically by name (case-insensitive)
  const sortedLists = useMemo(() => {
    return [...availableLists].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    )
  }, [availableLists])

  const selectedLists = sortedLists.filter((list) =>
    selectedKeys.has(list.reportListKey),
  )
  const singleSelected =
    selectedLists.length === 1 ? selectedLists[0] : null

  const toggleSelection = (key: string) => {
    const newSelected = new Set(selectedKeys)
    if (newSelected.has(key)) {
      newSelected.delete(key)
    } else {
      newSelected.add(key)
    }
    setSelectedKeys(newSelected)
  }

  const handleConfirm = () => {
    onConfirm(selectedLists)
    setSelectedKeys(new Set())
    onOpenChange(false)
  }

  const handleCancel = () => {
    setSelectedKeys(new Set())
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("dialogs.openList.title")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Left: Available Lists */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">{t("dialogs.openList.selectLists")}</h3>
            <div className="max-h-[400px] overflow-auto rounded border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]" />
                    <TableHead>Report List</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedLists.map((list) => (
                    <TableRow
                      key={list.reportListKey}
                      className="cursor-pointer"
                      onClick={() => toggleSelection(list.reportListKey)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedKeys.has(list.reportListKey)}
                          onCheckedChange={() =>
                            toggleSelection(list.reportListKey)
                          }
                        />
                      </TableCell>
                      <TableCell>{list.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Right: Preview of single selected list */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">{t("actions.preview")}</h3>
            <div className="max-h-[400px] overflow-auto rounded border">
              {singleSelected ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{singleSelected.name}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {singleSelected.reportListItems.map((item) => (
                      <TableRow key={item.reportListItemKey}>
                        <TableCell>{item.displayName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
                  {selectedKeys.size === 0
                    ? t("dialogs.openList.selectNoneToPreview")
                    : t("dialogs.openList.selectOneToPreview")}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={selectedKeys.size === 0}>
            {t("reportList.open")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
